import React, { useState, useEffect, useRef } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { logOut } from "../../shared/_services/auth.services";
import { Link, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { useSnackbar } from "notistack";
import { getOnboardingDetails, getNotifications, markNotificationSeen, getLastMessageData, createPlaidLinkToken, exchangePlaidPublicToken } from "../../shared/_services/common.services";
import Notifications from "../Notifications";
import UserChatScreen from "../UserChatScreen";

import { useDispatch, useSelector } from "react-redux";
import { setNewUnreadMessage } from "../../redux/chatReducer";

import chatAnimatedIcon from "../../assets/images/chatAnimatedIcon.gif";

import socket from "../../shared/_helpers/socket";
import bankSvg from "../../assets/images/Bank Icon.svg"
import { usePlaidLink } from "react-plaid-link";
import PlaidFallbackModal from "../PlaidFallbackModal";

function DashboardHeader() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const newUnreadMessage = useSelector((state) => state.unseenMessages.newUnreadMessage);
  const { enqueueSnackbar } = useSnackbar();
  const [notificationPopupShow, setNotificationPopupShow] = useState(false);
  const [userChatScreenPopupShow, setUserChatScreenPopupShow] = useState(false);
  const [confirmLogoutPopupShow, setConfirmLogoutPopupShow] = useState(false);
  const confirmLogoutPopupClose = () => setConfirmLogoutPopupShow();
  const [notification, setNotification] = useState([]);
  const [newNotificaiton, setNewNotification] = useState(false);
  const [videoPopupShow, setVideoPopupShow] = useState(false);
  const videoPopupClose = () => setVideoPopupShow(false);
  const videoPopupOpen = () => setVideoPopupShow(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const chatRef = useRef(null);

  const [linkToken, setLinkToken] = useState(null);
  const token = JSON.parse(localStorage.getItem("currentUser"))?.token;
  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const payment_status = JSON.parse(localStorage.getItem("currentUser"))?.user?.payment_status;

  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [modalVariant, setModalVariant] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalSecondaryMessage, setModalSecondaryMessage] = useState('');

  // Close chatbot on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatRef.current && !chatRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Initialize Plaid Link Token
  useEffect(() => {
    if (!token || !userId) return;

    createPlaidLinkToken(userId, `${process.env.REACT_APP_URL}/dashboard`)
      .then((link_token) => setLinkToken(link_token))
      .catch((error) => {
        console.error("Error creating Plaid link token:", error);
        setModalVariant('error');
        setModalMessage('We couldn’t connect your accounts.');
        setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
        setShowPlaidModal(true);
      });
  }, [token, userId]);

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Plaid Link configuration
const { open, ready } = usePlaidLink({
  token: linkToken,

  onSuccess: async (public_token) => {
    const startTime = Date.now();

    try {
      // SHOW INFO MODAL FIRST
      setModalVariant('info');
      setModalMessage('Connecting your accounts…');
      setModalSecondaryMessage(
        'This usually takes just a few seconds.\nYour information is encrypted and secure.'
      );
      setShowPlaidModal(true);

      // API CALL
      await exchangePlaidPublicToken(public_token, userId);

      // ENSURE INFO STATE STAYS FOR MIN 2 SECONDS
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await wait(2000 - elapsed);
      }

      // SUCCESS STATE
      setModalVariant('success');
      setModalMessage('Your accounts are connected!');
      setModalSecondaryMessage(
        'We pulled in the details we could. You can add or edit anything manually — estimates are totally fine.'
      );

    } catch (error) {
      console.error("Error exchanging Plaid token:", error);

      // ENSURE INFO STATE STAYS FOR MIN 2 SECONDS EVEN ON ERROR
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await wait(2000 - elapsed);
      }

      setModalVariant('error');
      setModalMessage('We couldn’t connect your accounts.');
      setModalSecondaryMessage(
        'This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.'
      );
      setShowPlaidModal(true);
    }
  },

  onExit: (error) => {
    if (error) {
      setModalVariant('warning');
      setModalMessage('Your bank didn’t authorize the connection.');
      setModalSecondaryMessage(
        'This is common if your bank requires extra verification. You can try again, or enter your info manually — your advisor can help.'
      );
      setShowPlaidModal(true);
    }
  },
});



  const openConfirmLogoutPopup = () => {
    setConfirmLogoutPopupShow(true);
    setIsMenuOpen(false);
  };

  const handleVideoClick = () => {
    videoPopupOpen();
  }

  const handleLogout = async () => {
    // logOut clears the stored session itself, whatever the server answered,
    // so this always navigates. Gating the clear on a 200 is what made logout
    // unusable: the request had the wrong shape for SEC-115 (#72), so the
    // branch never ran and the only feedback was an error toast. Even with the
    // right shape a 15-minute access token can expire in an open tab, and a
    // logout that refuses to work because the server said 401 is a worse
    // outcome than an orphaned refresh token nobody holds a copy of.
    const data = await logOut();

    if (data?.status !== 200) {
      enqueueSnackbar("Signed out on this device, but the server could not end the session.", {
        variant: "warning",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }

    navigate("/login");
  };

  const handleNotificationsClick = async () => {
    setNotificationPopupShow(true);
    setNewNotification(false)

    for (const notif of notification) {
      try {
        await markNotificationSeen(notif._id);
      } catch (error) {
        console.error(`Error marking notification ${notif._id} as seen:`, error);
      }
    }
  };


  const handleUserChatScreenClick = () => {
    setUserChatScreenPopupShow(true);
    dispatch(setNewUnreadMessage(false)); // Reset unread message notification
  };

  const getData = async () => {
    let data = await getOnboardingDetails();

    if (data.status === 200) {
      if (data.data.length === 0) navigate("/dashboard-zero");
      
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
  };

  const getNotificationData = async () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const userId = currentUser?.user?._id
    const data = await getNotifications(userId);

    if (data.status === 200) {
      const unseenNotifications = data?.data?.data?.filter(notification => !notification.seen);
      setNotification(unseenNotifications);
      if (unseenNotifications.length > 0) {
        setNewNotification(true)
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("No New Notifications", {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
       
      }
    }
  }

 

  const handleNotificationClose = (state) => {
    setNotificationPopupShow(state)
  }

  const handleUserChatScreenClose = (state) => {
    setUserChatScreenPopupShow(state)
  }

  useEffect(() => {
    getNotificationData();
    getData();
  }, []); // eslint-disable-line


  useEffect(() => {
    const fetchLastMessage = async () => {
      try {
        const response = await getLastMessageData();
        const lastMessage = response?.data?.data?.lastMessage;
        const userId = JSON.parse(localStorage.getItem("currentUser"))?.user._id;

        if (lastMessage) {
          const isMessageFromUser = lastMessage.sender._id === userId;
          const isMessageUnread = !lastMessage.readBy.includes(userId);

          dispatch(setNewUnreadMessage(!isMessageFromUser && isMessageUnread));
        }
      } catch (error) {
        console.error("Error fetching last message:", error);
      }
    };

    fetchLastMessage(); // Fetch only once when the component loads

    socket.on("chat list updated", fetchLastMessage); // Refresh instantly when triggered

    return () => {
      socket.off("chat list updated", fetchLastMessage);
    };
  }, [dispatch]);


  return (
    <>
    <div className="dashboard-header mb-4">
      <div className="d-flex justify-content-between">
        <div className="d-flex dashboard-user-dtls">
          <div className="mr-4">
            <h5>Welcome,&nbsp;
              <span title={`${JSON.parse(localStorage.getItem("currentUser"))?.user?.firstName} ${JSON.parse(localStorage.getItem("currentUser"))?.user?.lastName}`}>
                {`${JSON.parse(localStorage.getItem("currentUser"))?.user?.firstName} ${JSON.parse(localStorage.getItem("currentUser"))?.user?.lastName}`.slice(0, 20) +
                  ((JSON.parse(localStorage.getItem("currentUser"))?.user?.firstName + JSON.parse(localStorage.getItem("currentUser"))?.user?.lastName).length > 20 ? "..." : "")
                }
              </span>
            </h5>
            <p className="text-14 f-400 mb-0 text-secondary">Here is your Financial Overview</p>
          </div>
        </div>
        <div className="d-flex gap-15 align-items-center">
         
          {/* MOVED: Configure Plaid - Now with TEXT */}
          <a
            className="check_credit_score d-flex align-items-center gap-2"
            onClick={() => {
              // payment_status is a boolean on the backend (model/user.js);
              // this used to compare against the string "false", which a
              // boolean never equals, so a lapsed subscriber never got
              // routed to /package.
              if (!payment_status) {
                navigate("/package", { state: { from: "/" } });
              } else {
                open();
              }
            }}
            style={{ cursor: ready ? "pointer" : "not-allowed", textDecoration: 'none', color: '#21151B' }}
            title="Configure Plaid"
            disabled={!ready}
          >
            <img style={{ width: "28px", height: "24px" }} src={bankSvg} alt="Plaid" />
            <span>Configure Plaid</span>
          </a>

          {/* MOVED: Check Credit Score - Now ICON-ONLY*/}
          <div
            className="circle-button"
            title="Check Credit Score"
          >
            <a href="https://consumer.experian.in/ECV-OLN/view/angular/#/signIn" target="_blank">
              <svg width="20" height="16" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 17.3333C12.9391 17.3333 11.9217 16.9119 11.1716 16.1618C10.4214 15.4116 10 14.3942 10 13.3333C10 11.84 10.8133 10.5333 12 9.85333L24.9467 2.36L17.5733 15.1333C16.9067 16.44 15.56 17.3333 14 17.3333ZM14 0C16.4133 0 18.6667 0.666667 20.6267 1.76L17.8267 3.37333C16.6667 2.92 15.3333 2.66667 14 2.66667C11.171 2.66667 8.45791 3.79047 6.45752 5.79086C4.45714 7.79125 3.33333 10.5044 3.33333 13.3333C3.33333 16.28 4.52 18.9467 6.45333 20.8667H6.46666C6.98666 21.3867 6.98666 22.2267 6.46666 22.7467C5.94666 23.2667 5.09333 23.2667 4.57333 22.76C3.33336 21.5235 2.34991 20.0542 1.67948 18.4364C1.00905 16.8187 0.664855 15.0845 0.666664 13.3333C0.666664 9.79711 2.07142 6.40573 4.57191 3.90524C7.07239 1.40476 10.4638 0 14 0ZM27.3333 13.3333C27.3333 17.0133 25.84 20.3467 23.4267 22.76C22.9067 23.2667 22.0667 23.2667 21.5467 22.7467C21.4231 22.6233 21.325 22.4768 21.2581 22.3155C21.1912 22.1542 21.1567 21.9813 21.1567 21.8067C21.1567 21.632 21.1912 21.4591 21.2581 21.2978C21.325 21.1365 21.4231 20.99 21.5467 20.8667C22.5383 19.8791 23.3245 18.705 23.86 17.412C24.3955 16.119 24.6697 14.7328 24.6667 13.3333C24.6667 12 24.4133 10.6667 23.9467 9.46667L25.56 6.66667C26.6667 8.66667 27.3333 10.9067 27.3333 13.3333Z" fill="#21151B" />
              </svg>
            </a>
          </div>
          {/* <Lottie animationData={chatAnimation} loop={true} style={{ width: 80, height: 80, margin:"-12px -10px 0px 10px"}} /> */}
          <div className="circle-button" onClick={handleVideoClick} title="Video">
            <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.457993 3.10826C0.457993 2.38323 0.746012 1.68789 1.25869 1.17521C1.77137 0.662531 2.46671 0.374512 3.19174 0.374512H9.38824C10.1133 0.374512 10.8086 0.662531 11.3213 1.17521C11.834 1.68789 12.122 2.38323 12.122 3.10826V9.30476C12.122 10.0298 11.834 10.7251 11.3213 11.2378C10.8086 11.7505 10.1133 12.0385 9.38824 12.0385H3.19174C2.46671 12.0385 1.77137 11.7505 1.25869 11.2378C0.746012 10.7251 0.457993 10.0298 0.457993 9.30476V3.10826ZM15.9507 11.0784L12.851 8.93808V3.47276L15.9507 1.33242C16.7963 0.74776 17.9511 1.35429 17.9511 2.38218V10.0294C17.9511 11.0573 16.7971 11.6623 15.9507 11.0784Z" fill="url(#paint0_linear_1199_433)" />
              <defs>
                <linearGradient id="paint0_linear_1199_433" x1="9.20453" y1="0.374512" x2="9.20453" y2="12.0385" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#63363A" />
                  <stop offset="1" stopColor="#1A1D43" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={`circle-button ${newUnreadMessage ? "new-unreadmessage" : ""}`} onClick={handleUserChatScreenClick} title="Chat">
            <img width={40} src={chatAnimatedIcon} />
          </div>
          <UserChatScreen onClose={handleUserChatScreenClose} showModal={userChatScreenPopupShow} />
          <div className={`circle-button ${newNotificaiton ? "new-notification" : ""}`} onClick={handleNotificationsClick} title="Notifications">
            <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.94591 0.800293C7.27505 0.800293 5.67262 1.46404 4.49114 2.64552C3.30966 3.827 2.64591 5.42943 2.64591 7.10029V10.2755C2.64604 10.4151 2.61369 10.5528 2.55141 10.6778L1.00611 13.7675C0.930626 13.9184 0.894982 14.0862 0.902566 14.2548C0.910149 14.4234 0.960709 14.5872 1.04944 14.7308C1.13818 14.8744 1.26214 14.9929 1.40955 15.075C1.55697 15.1572 1.72294 15.2003 1.89171 15.2003H16.0001C16.1689 15.2003 16.3349 15.1572 16.4823 15.075C16.6297 14.9929 16.7536 14.8744 16.8424 14.7308C16.9311 14.5872 16.9817 14.4234 16.9893 14.2548C16.9968 14.0862 16.9612 13.9184 16.8857 13.7675L15.3413 10.6778C15.2787 10.5529 15.2461 10.4152 15.2459 10.2755V7.10029C15.2459 5.42943 14.5822 3.827 13.4007 2.64552C12.2192 1.46404 10.6168 0.800293 8.94591 0.800293ZM8.94591 17.9003C8.38733 17.9006 7.8424 17.7276 7.38623 17.4052C6.93006 17.0829 6.58511 16.6269 6.39891 16.1003H11.4929C11.3067 16.6269 10.9618 17.0829 10.5056 17.4052C10.0494 17.7276 9.5045 17.9006 8.94591 17.9003Z" fill="url(#paint0_linear_1199_1366)" />
              <defs>
                <linearGradient id="paint0_linear_1199_1366" x1="8.94591" y1="0.800293" x2="8.94591" y2="17.9003" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#63363A" />
                  <stop offset="1" stopColor="#1A1D43" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <Notifications onClose={handleNotificationClose} showModal={notificationPopupShow} />

          {/* Logout Button */}
          <div className="circle-button" onClick={openConfirmLogoutPopup} title="Logout">
            <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.93542 1.01928C9.60672 0.643079 10.819 2.07948 10.819 3.49968V16.0997C10.819 17.5199 9.60672 18.9563 7.93542 18.5801C3.91962 17.6765 0.919022 14.0891 0.919022 9.79968C0.919022 5.51028 3.91962 1.92288 7.93542 1.01928ZM12.8827 6.46338C13.0515 6.29465 13.2804 6.19987 13.519 6.19987C13.7577 6.19987 13.9865 6.29465 14.1553 6.46338L16.8553 9.16338C17.024 9.33215 17.1188 9.56103 17.1188 9.79968C17.1188 10.0383 17.024 10.2672 16.8553 10.436L14.1553 13.136C13.9856 13.2999 13.7582 13.3906 13.5223 13.3886C13.2863 13.3865 13.0606 13.2919 12.8937 13.125C12.7268 12.9581 12.6322 12.7324 12.6301 12.4964C12.6281 12.2605 12.7188 12.0331 12.8827 11.8634L14.0464 10.6997H6.31902C6.08033 10.6997 5.85141 10.6049 5.68263 10.4361C5.51384 10.2673 5.41902 10.0384 5.41902 9.79968C5.41902 9.56098 5.51384 9.33207 5.68263 9.16328C5.85141 8.9945 6.08033 8.89968 6.31902 8.89968H14.0464L12.8827 7.73598C12.714 7.5672 12.6192 7.33833 12.6192 7.09968C12.6192 6.86103 12.714 6.63215 12.8827 6.46338Z" fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>

      {/* confirm logout popup */}
      <Modal
        size="md"
        className="confirmLogoutPopup customMiniPopup"
        show={confirmLogoutPopupShow}
        onHide={confirmLogoutPopupClose}
        aria-labelledby="confirmLogoutPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3">  </h4>
            <Link className="close-popup-btn" onClick={confirmLogoutPopupClose}>
              <i className="iconoir-xmark"></i>
            </Link>
          </div>
          <h5 className="mt-3 mb-2 text-center f-400 text-secondary">You are attempting to logout of Astra.</h5>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary" onClick={handleLogout}>
              <i className="fa fa-check mr-2" aria-hidden="true"></i>
              Logout
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        size="md"
        className="videoPopup customMiniPopup"
        show={videoPopupShow}
        onHide={videoPopupClose}
        aria-labelledby="videoPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body p-0">
          <div className="d-flex justify-content-between align-items-center p-3">
            <div>
              <h4 className="mb-0 text-astra f-700">Welcome to Astra</h4>
              <p className="text-secondary f-500 mb-0">Your Quick Start Guide</p>
            </div>
            <Link className="close-popup-btn" onClick={videoPopupClose}>
              <i className="iconoir-xmark"></i>
            </Link>
          </div>
          <div className="onboarding-vdo mt-0 px-4">
            <video className="w-100 rounded-lg" src={`${process.env.REACT_APP_BACKEND}/uploads/WelcometoAstraStars.mp4`} controls loop autoPlay></video>
          </div>
        </div>
      </Modal>
    </div>

    {showPlaidModal && (
        <PlaidFallbackModal variant={modalVariant} message={modalMessage} secondaryMessage={modalSecondaryMessage} onClose={() => setShowPlaidModal(false)} onTryAgain={open} />
      )}
    </>
  );
}

export default DashboardHeader;
